<script setup lang="ts">
  import { ref } from 'vue'
  import { useDateFormat } from '@vueuse/core'
  import { useFlipbooksMenuStore } from '@/stores/list-item-menu'
  import { useFlipbooksStore } from '@/stores/flipbooks'
  import { useSnackbarStore } from '@/stores/snackbar'
  import { useBrowserStore } from '@/stores/browser'
  import type { TProjectFlipbook } from '@/types/flipbooks'
  import DesHover from '@/components/DesHover.vue'
  import DesHoverMenu from '@/components/DesHoverMenu.vue'
  import DesButtonIcon from '@/components/button/DesButtonIcon.vue'
  import DesEditName from '@/components/DesEditName.vue'

  defineProps<{
    item: TProjectFlipbook
    disableCheckboxes?: boolean
  }>()

  const flipbooks = useFlipbooksStore()
  const snack = useSnackbarStore()
  const browser = useBrowserStore()
  const { open } = useFlipbooksMenuStore()

  const formatDate = (date: string) => useDateFormat(date, 'MMMM DD, YYYY', { locales: 'en-US' })
  const tile = ref<HTMLElement | null>(null)

  const preview = async (project: TProjectFlipbook) => {
    await browser.openNewWindowTab(await flipbooks.buildFlipbookUrl(project.id, project.token, 'FP'))
  }

  const renameProject = async (project: TProjectFlipbook, name: string) => {
    const oldName = project.name
    project.name = name

    const res = await flipbooks.renameFlipbook(project.id, project.name)
    if (res.status === 'error') {
      snack.add(res.message)
      project.name = oldName
    }
  }
</script>

<template>
  <div class="flex flex-col gap-1.5" ref="tile">
    <div
      class="relative h-52 px-4 pt-4 bg-white-gray rounded-xl justify-center items-start inline-flex overflow-hidden cursor-pointer"
    >
      <div class="h-64 flex-col justify-start items-center inline-flex">
        <img
          v-once
          class="rounded-md max-h-full max-w-full w-auto h-auto shadow-sm"
          :src="item.thumbnail_url"
          loading="lazy"
          alt=""
          width="0"
          height="0"
        />
      </div>
      <DesHover data-sortable-handler @click.stop="preview(item)">
        <DesHoverMenu>
          <DesButtonIcon @click.stop="preview(item)" icon="fa-light fa-eye" />
          <DesButtonIcon @click.stop="tile && open(item, tile)" icon="fa-light fa-ellipsis" />
        </DesHoverMenu>
      </DesHover>
    </div>
    <div>
      <DesEditName :name="item.name" @edit="(newValue: string) => renameProject(item, newValue)" />
      <div class="mt-1 text-black-40 text-xs leading-none">
        {{ item.updated_at ? formatDate(item.created_at).value : '' }}
      </div>
    </div>
  </div>
</template>
